import React, { createContext, useContext, useReducer, useEffect } from 'react';
import Joyride from 'react-joyride';
import { useAuth } from './AuthContext';

const TourContext = createContext();

const tourReducer = (state, action) => {
  switch (action.type) {
    case 'START_TOUR':
      return {
        ...state,
        currentTour: action.payload,
        isRunning: true,
        stepIndex: 0
      };
    case 'COMPLETE_TOUR':
      return {
        ...state,
        completedTours: [...state.completedTours, action.payload],
        isRunning: false,
        currentTour: null
      };
    case 'SKIP_TOUR':
      return {
        ...state,
        skippedTours: [...state.skippedTours, action.payload],
        isRunning: false,
        currentTour: null
      };
    case 'UPDATE_STEP':
      return {
        ...state,
        stepIndex: action.payload
      };
    case 'STOP_TOUR':
      return {
        ...state,
        isRunning: false,
        currentTour: null
      };
    case 'LOAD_SAVED_DATA':
      return {
        ...state,
        completedTours: action.payload.completed || [],
        skippedTours: action.payload.skipped || []
      };
    default:
      return state;
  }
};

export const TourProvider = ({ children }) => {
  const { user } = useAuth();
  const [state, dispatch] = useReducer(tourReducer, {
    currentTour: null,
    isRunning: false,
    stepIndex: 0,
    completedTours: [],
    skippedTours: []
  });

  // Load saved tour data from localStorage
  useEffect(() => {
    if (user) {
      const savedData = localStorage.getItem(`tours_${user.uid}`);
      if (savedData) {
        dispatch({ type: 'LOAD_SAVED_DATA', payload: JSON.parse(savedData) });
      }
    }
  }, [user]);

  // Save tour data to localStorage
  useEffect(() => {
    if (user && (state.completedTours.length > 0 || state.skippedTours.length > 0)) {
      localStorage.setItem(`tours_${user.uid}`, JSON.stringify({
        completed: state.completedTours,
        skipped: state.skippedTours
      }));
    }
  }, [state.completedTours, state.skippedTours, user]);

  // iOS-like styling configuration
  const joyrideStyles = {
    options: {
      primaryColor: '#007AFF',
      backgroundColor: '#ffffff',
      overlayColor: 'rgba(0, 0, 0, 0.4)',
      spotlightShadow: '0 0 20px rgba(0, 0, 0, 0.5)',
      borderRadius: 12,
      arrowColor: '#ffffff',
      textColor: '#333333',
      zIndex: 10000
    },
    tooltip: {
      borderRadius: 12,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
      padding: '20px 24px',
      maxWidth: '320px',
      fontSize: '16px',
      lineHeight: '1.5'
    },
    tooltipTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '8px',
      color: '#1a1a1a'
    },
    tooltipContent: {
      fontSize: '16px',
      lineHeight: '1.5',
      color: '#333333'
    },
    buttonNext: {
      backgroundColor: '#007AFF',
      borderRadius: '8px',
      padding: '10px 20px',
      fontSize: '16px',
      fontWeight: '500',
      border: 'none',
      outline: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    buttonBack: {
      backgroundColor: 'transparent',
      border: '2px solid #007AFF',
      borderRadius: '8px',
      padding: '8px 18px',
      fontSize: '16px',
      fontWeight: '500',
      color: '#007AFF',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    buttonSkip: {
      backgroundColor: 'transparent',
      border: 'none',
      color: '#666666',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer',
      padding: '8px 12px'
    },
    spotlight: {
      borderRadius: '8px',
      transition: 'all 0.3s ease'
    },
    overlay: {
      transition: 'all 0.3s ease'
    }
  };

  const handleJoyrideCallback = (data) => {
    const { action, index, type, status } = data;
    
    if (type === 'tour:end') {
      if (action === 'skip' || status === 'skipped') {
        dispatch({ type: 'SKIP_TOUR', payload: state.currentTour.id });
      } else if (status === 'finished') {
        dispatch({ type: 'COMPLETE_TOUR', payload: state.currentTour.id });
      }
    } else if (type === 'step:after') {
      dispatch({ type: 'UPDATE_STEP', payload: index + 1 });
    }
  };

  return (
    <TourContext.Provider value={{ state, dispatch }}>
      {children}
      <Joyride
        steps={state.currentTour?.steps || []}
        run={state.isRunning}
        stepIndex={state.stepIndex}
        continuous={true}
        showProgress={true}
        showSkipButton={true}
        disableOverlayClose={true}
        disableCloseOnEsc={false}
        hideCloseButton={false}
        scrollToFirstStep={true}
        scrollOffset={100}
        styles={joyrideStyles}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip tour'
        }}
        callback={handleJoyrideCallback}
        floaterProps={{
          disableAnimation: false,
          styles: {
            floater: {
              filter: 'drop-shadow(0 8px 32px rgba(0, 0, 0, 0.15))'
            }
          }
        }}
      />
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};